import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import customerAuthService, {
  CustomerLoginCredentials,
  CustomerProfile,
} from '@services/customer-auth.service';
import { getApiErrorMessage } from '@utils/apiError';

interface PortalAuthContextType {
  customer: CustomerProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: CustomerLoginCredentials) => Promise<CustomerProfile>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const PortalAuthContext = createContext<PortalAuthContextType | undefined>(undefined);

export const PortalAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearPortalAuth = useCallback(() => {
    setCustomer(null);
    customerAuthService.clearSession();
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!customerAuthService.isAuthenticated()) {
      setCustomer(null);
      return;
    }
    const profile = await customerAuthService.getProfile();
    setCustomer(profile);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      if (!customerAuthService.isAuthenticated()) {
        if (!cancelled) {
          setCustomer(null);
          setIsLoading(false);
        }
        return;
      }
      try {
        const profile = await customerAuthService.getProfile();
        if (!cancelled) setCustomer(profile);
      } catch {
        if (!cancelled) clearPortalAuth();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [clearPortalAuth]);

  const login = useCallback(async (credentials: CustomerLoginCredentials) => {
    const response = await customerAuthService.login(credentials);
    setCustomer(response.customer);
    return response.customer;
  }, []);

  const logout = useCallback(async () => {
    try {
      await customerAuthService.logout();
    } catch (err) {
      getApiErrorMessage(err, 'Logout failed');
      clearPortalAuth();
    } finally {
      setCustomer(null);
    }
  }, [clearPortalAuth]);

  return (
    <PortalAuthContext.Provider
      value={{
        customer,
        isAuthenticated: !!customer,
        isLoading,
        login,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </PortalAuthContext.Provider>
  );
};

export const usePortalAuth = (): PortalAuthContextType => {
  const context = useContext(PortalAuthContext);
  if (!context) {
    throw new Error('usePortalAuth must be used within PortalAuthProvider');
  }
  return context;
};
